import { signal } from "@angular/core";
import {
  Calendar1,
  ChartLine,
  CloudDownload,
  CloudUpload,
  Database,
  Factory,
  File,
  FileText,
  FileUp,
  FolderTree,
  GraduationCap,
  Group,
  Handshake,
  Home,
  Hourglass,
  Info,
  KeyRound,
  LogOut,
  Network,
  Settings,
  User,
  Users,
} from "lucide-angular";
import { BenutzerRouteDefs } from "../../../../../features/benutzerprofil/routing/BenutzerRouteDefs";
import { HomeRouteDefs } from "../../../../../features/home/routing/HomeRouteDefs";
import { RollenRouteDefs } from "../../../../../features/rollen/routing/RollenRouteDefs";
import { NavDto } from "./NavDto";

import { PermissionMatrixDto } from "../../../../../api";
import { AuthenticationRouteDefs } from "../../../../../features/authentication/routing/AuthenticationRouteDefs";
import * as Entities from "../../../../../features/automatic-form/entities/index";
import { AktionRouteDefs } from "../../../../../features/bmsgpk/features/aktion/routing/AktionRouteDefs";
import { FilemanagementRouteDefs } from "../../../../../features/filemanagement/routing/FilemanagementRouteDefs";
import { EntsendendeOrganisationRouteDefs } from "../../../../../features/ib-betrieb/features/entsendendeorganisation/routing/EntsendendeOrganisationRouteDefs";
import { FinanzstelleRouteDefs } from "../../../../../features/ib-betrieb/features/finanzstellen/routing/FinanzstelleRouteDefs";
import { GeschaeftsfuehrungRouteDefs } from "../../../../../features/ib-betrieb/features/geschaeftsfuehrung/routing/GeschaeftsfuehrungRouteDefs";
import { IBBetriebeRouteDefs } from "../../../../../features/ib-betrieb/features/ibbetriebe/routing/IBBetriebeRouteDefs";
import { IBBetriebZweigwerkRouteDefs } from "../../../../../features/ib-betrieb/features/ibbetriebzweigwerk/routing/IBBetriebZweigwerkRouteDefs";
import { KontaktRouteDefs } from "../../../../../features/ib-betrieb/features/kontakt/routing/KontaktRouteDefs";
import { InfoboxRouteDefs } from "../../../../../features/infobox/routing/InfoboxRouteDefs";
import { AbfertigungsrefundierungRouteDefs } from "../../../../../features/sms/feature/abfertigungsrefundierung/routing/AbfertigungsrefundierungRouteDefs";
import { IBLWirkungsmessungRouteDefs } from "../../../../../features/sms/feature/iblwirkungsmessung/routing/IBLWirkungsmessungRouteDefs";
import { SchnittstellendatenRouteDefs } from "../../../../../features/sms/feature/schnittstellendaten/routing/SchnittstellendatenRouteDefs";
import { IBLTaetigkeitsberichtRouteDefs } from "../../../../../features/sms/feature/taetigkeitsbericht/routing/IBLTaetigkeitsberichtRouteDefs";
import { WirtschaftlicherStatusRouteDefs } from "../../../../../features/sms/feature/wirtschaftlicherstatus/routing/WirtschaftlicherStatusRouteDefs";
import { TimemachineRouteDefs } from "../../../../../features/timemachine/routing/TimemachineRouteDefs";
import { SessionService } from "../../../../services/session.service";

export function ALL_NAV_ITEMS(): NavDto[] {
  return [
    new NavDto({
      name: "Rollen",
      route: signal(RollenRouteDefs.getFQRRollenList()),
      icon: Group,
      permission: "rolleRead",
    }),
    new NavDto({
      name: "Termine & Benachrichtigungen",
      route: signal(HomeRouteDefs.getFQRHomeTermineBenachrichtigungen()),
      icon: Calendar1,
    }),
    new NavDto({
      name: "Infobox",
      route: signal(InfoboxRouteDefs.getFQRList()),
      icon: Info,
    }),
    new NavDto({
      name: "Alle Benutzer",
      route: signal(BenutzerRouteDefs.getFQRBenutzerList()),
      icon: Users,
      permission: "benutzerRead",
    }),
    new NavDto({
      name: "Stammdaten",
      icon: Database,
      children: ALL_STAMMDATEN_NAV(),
    }),
    // Infobox is always accessible, no permission required
    // (moved under Termine & Benachrichtigungen)
    new NavDto({
      name: "Sachbereiche",
      icon: FolderTree,
      children: ALL_SMS_NAV,
    }),
    new NavDto({
      name: "Integrativer Betrieb",
      icon: Factory,
      children: ALL_IBBETRIEBE_NAV,
    }),
    new NavDto({
      name: "Schnittstellendaten",
      route: signal(SchnittstellendatenRouteDefs.BASE_PATH),
      icon: Network,
      permission: "csvUploadDisplay",
    }),
    new NavDto({
      name: "Dateien",
      icon: File,
      children: ALL_FILE_NAV,
    }),
    new NavDto({
      name: "Zeitmaschine",
      route: signal(TimemachineRouteDefs.getFQTimeMachinePath()),
      icon: Hourglass,
      permission: "administratorDisplay",
    }),
    new NavDto({
      name: "Home",
      route: signal(HomeRouteDefs.getFQRHome()),
      icon: Home,
      location: "aside-fixed-b",
    }),
    new NavDto({
      name: "shell.breadcrumbs.userMenu.profile",
      route: SessionService.UserRoute,
      icon: User,
      location: "user-dropdown",
    }),
    new NavDto({
      name: "shell.breadcrumbs.userMenu.settings",
      route: SessionService.UserSettings,
      icon: Settings,
      location: "user-dropdown",
    }),
    new NavDto({
      name: "shell.breadcrumbs.userMenu.changePassword",
      route: SessionService.UserChangePwd,
      icon: KeyRound,
      location: "user-dropdown",
    }),
    new NavDto({
      name: "shell.breadcrumbs.userMenu.logout",
      route: signal(AuthenticationRouteDefs.getFQRLogout()),
      icon: LogOut,
      location: "user-dropdown",
    }),
    new NavDto({
      name: "shell.breadcrumbs.userMenu.unshadow",
      route: signal("none"),
      icon: LogOut,
      location: "user-dropdown",
    }),
  ];
}

export function ALL_STAMMDATEN_NAV(): NavDto[] {
  return [
    new NavDto({
      name: "BMASGPK",
      icon: FileText,
      level: 1,
      children: [
        new NavDto({
          name: "Aktion",
          route: signal(AktionRouteDefs.getFQR()),
          icon: FileText,
          permission: "aktionDisplay",
          level: 2,
        }),
      ],
    }),
    ...Object.values(Entities).map(
      (e) =>
        new NavDto({
          name: new e().entityNamePlural,
          route: signal("stammdaten" + "/" + e.path),
          icon: FileText,
          permission: (new e().entityPermissionName + "Display") as keyof PermissionMatrixDto,
          level: 1,
        }),
    ),
    new NavDto({
      name: "Integrative Betriebe",
      route: signal(IBBetriebeRouteDefs.getFQR()),
      icon: FileText,
      permission: "iBBetriebRead",
      level: 1,
    }),
    new NavDto({
      name: "Kontakte",
      route: signal(KontaktRouteDefs.getFQR()),
      icon: FileText,
      permission: "kontaktRead",
      level: 1,
    }),
    new NavDto({
      name: "Finanzstellen",
      route: signal(FinanzstelleRouteDefs.getFQR()),
      icon: FileText,
      permission: "finanzstelleRead",
      level: 1,
    }),
    new NavDto({
      name: "Zweigwerke",
      route: signal(IBBetriebZweigwerkRouteDefs.getFQR()),
      icon: FileText,
      permission: "iBBetriebZweigwerkRead",
      level: 1,
    }),
    new NavDto({
      name: "Geschäftsführung",
      route: signal(GeschaeftsfuehrungRouteDefs.getFQR()),
      icon: FileText,
      permission: "geschaeftsfuehrungRead",
      level: 1,
    }),
    new NavDto({
      name: "Entsendende Organisation",
      route: signal(EntsendendeOrganisationRouteDefs.getFQR()),
      icon: FileText,
      permission: "entsendendeOrganisationRead",
      level: 1,
    }),
  ];
}

export const ALL_IBBETRIEBE_NAV: NavDto[] = [
  new NavDto({
    name: "CSV Schnittstelle",
    route: signal(FilemanagementRouteDefs.getFQRFKTList()),
    icon: FileUp,
    permission: "csvUploadDisplay",
    level: 1,
  }),
];

export const ALL_SMS_NAV: NavDto[] = [
  new NavDto({
    name: "IBL",
    icon: GraduationCap,
    level: 1,
    children: [
      new NavDto({
        name: "Wirkungsmessung",
        route: signal(IBLWirkungsmessungRouteDefs.getFQR()),
        icon: ChartLine,
        permission: "iBLWirkungsmessungDisplay",
        level: 2,
      }),
      new NavDto({
        name: "Tätigkeitsbericht",
        route: signal(IBLTaetigkeitsberichtRouteDefs.getFQR()),
        icon: FileText,
        level: 2,
      }),
    ],
  }),
  new NavDto({
    name: "Wirtschaftl. Status",
    route: signal(WirtschaftlicherStatusRouteDefs.getFQR()),
    icon: FileText,
    permission: "wirtschaftlicherStatusRead",
    level: 1,
  }),
  new NavDto({
    name: "Abfertigung",
    route: signal(AbfertigungsrefundierungRouteDefs.BASE_PATH),
    icon: Handshake,
    permission: "abfertigungsrefundierungRead",
    level: 1,
  }),
];

export const ALL_FILE_NAV: NavDto[] = [
  new NavDto({
    name: "Datei-Upload",
    route: signal(FilemanagementRouteDefs.getFQRUpload()),
    icon: CloudUpload,
    permission: "fileRecordCreate",
    level: 1,
  }),
  new NavDto({
    name: "Datei-Liste",
    route: signal(FilemanagementRouteDefs.getFQRList()),
    icon: CloudDownload,
    permission: "fileRecordRead",
    level: 1,
  }),
];
